"use client";

import { useState, useCallback, useRef, memo } from "react";
import { IconHeart } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import {
  useAddToFavorites,
  useRemoveFavorite,
} from "@/hooks/favorite/useFavorites";
import { useAuthModal } from "@/hooks/auth/useAuthModal";
import { useUser } from "@/hooks/user/useUser";
import { useToast } from "@/hooks/useToast";
import { FavoriteSvcType } from "@/components/favorites/favorite.type";

export type FavoriteIconButtonProps = {
  itemId: number;
  svcType: FavoriteSvcType;
  ariaLabel?: string;
  className?: string;
  active?: boolean;
};

const FavoriteIconButton = ({
  itemId,
  svcType,
  ariaLabel,
  className,
  active: initialActive = false,
}: FavoriteIconButtonProps) => {
  const { toast } = useToast();
  const { data: user } = useUser();
  const { openLogin } = useAuthModal();
  const [optimisticActive, setOptimisticActive] = useState<boolean | null>(
    null,
  );

  const prevItemRef = useRef({
    itemId,
    svcType,
    initialActive,
    userId: user?.id,
  });
  if (
    prevItemRef.current.itemId !== itemId ||
    prevItemRef.current.svcType !== svcType ||
    prevItemRef.current.initialActive !== initialActive ||
    prevItemRef.current.userId !== user?.id
  ) {
    prevItemRef.current = {
      itemId,
      svcType,
      initialActive,
      userId: user?.id,
    };
    setOptimisticActive(null);
  }

  const active = Boolean(user) && (optimisticActive ?? initialActive);

  // triggers the pop animation only when going false -> true
  const [justActivated, setJustActivated] = useState(false);
  const popTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: addToFavorites, isPending: isAdding } = useAddToFavorites();
  const { mutate: removeFavorite, isPending: isRemoving } = useRemoveFavorite();
  const isPending = isAdding || isRemoving;

  const triggerPop = useCallback(() => {
    if (popTimeoutRef.current) clearTimeout(popTimeoutRef.current);
    setJustActivated(true);
    popTimeoutRef.current = setTimeout(() => setJustActivated(false), 300);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!user) {
        void openLogin("email");
        return;
      }

      if (isPending) return;

      if (active) {
        setOptimisticActive(false);
        removeFavorite(
          { itemId, svcType },
          {
            onError: () => {
              setOptimisticActive(true);
              toast({
                title: "Failed to remove from favorites",
                variant: "destructive",
              });
            },
          },
        );
      } else {
        setOptimisticActive(true);
        triggerPop();
        addToFavorites(
          { itemId, svcType },
          {
            onError: () => {
              setOptimisticActive(false);
              toast({
                title: "Failed to add to favorites",
                variant: "destructive",
              });
            },
          },
        );
      }
    },
    [
      user,
      openLogin,
      isPending,
      active,
      itemId,
      svcType,
      addToFavorites,
      removeFavorite,
      toast,
      triggerPop,
    ],
  );

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? (active ? "Remove from favorites" : "Save item")}
      onClick={handleClick}
      className={cn(
        "shadow-md! flex h-8 w-8 items-center justify-center rounded-full border bg-white",
        "transition-transform duration-150 ease-out active:scale-90",
        active
          ? "text-error hover:text-error-foreground"
          : "text-strong hover:text-error-foreground",
        className,
      )}
    >
      <IconHeart
        className={cn(
          "size-6 transition-all duration-200 ease-out",
          justActivated && "animate-heart-pop",
        )}
        strokeWidth={1.8}
        fill={active ? "currentColor" : "none"}
      />
    </button>
  );
};

export default memo(FavoriteIconButton);
